import { formatters } from "@fraction/shared";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import { CheckIcon, ListIcon, Loader2, XIcon } from "lucide-react";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import fraction, { type ChecklistApp } from "src/api/fraction";
import { Button } from "src/components/ui/button";
import { APPLICATION_KEY, SINGLE_APPLICATION_KEY, useApplicationMutations } from "src/hooks/useApplication";
import {
  type PendingEntry,
  useApplicationPendingChangesStore,
  useHasAnyPending,
  usePendingApplicationId,
  usePendingCount,
  usePendingSaveState,
  usePendingSavingCount,
} from "src/stores/applicationPendingChanges";
import { useModalStackStore } from "src/stores/modalStackStore";
import { runSaver, type SaverName } from "src/stores/pendingChangesSavers";
import { SERIALIZERS } from "src/stores/pendingChangesSerializers";
import { cn } from "src/utilities/shadcnUtils";

/** How long the "Saved" acknowledgement stays up before the bar disappears. */
const SAVED_VISIBLE_MS = 1600;

export const PendingChangesSaveBar = memo(() => {
  const hasAny = useHasAnyPending();
  const saveState = usePendingSaveState();
  // Staying mounted through the save is the point: the entries are cleared the
  // moment Save is pressed, so gating purely on `hasAny` unmounted the bar
  // before it could show that anything was happening.
  if (!hasAny && saveState === "idle") return null;
  return <PendingChangesSaveBarInner />;
});

PendingChangesSaveBar.displayName = "PendingChangesSaveBar";

const PendingChangesSaveBarInner = memo(() => {
  const applicationId = usePendingApplicationId();
  const count = usePendingCount();
  const saveState = usePendingSaveState();
  const savingCount = usePendingSavingCount();
  const queryClient = useQueryClient();
  const { saveFields } = useApplicationMutations({ id: applicationId });
  const [isListOpen, setIsListOpen] = useState(false);
  const pillZIndex = useModalStackStore((store) => {
    const top = store.stack.at(-1);
    return top?.allowPendingChanges ? top.zIndex + 1 : undefined;
  });
  const isSaving = saveState === "saving";
  const savedTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      if (savedTimer.current !== undefined) {
        window.clearTimeout(savedTimer.current);
      }
    },
    []
  );

  const toggleList = useCallback(() => setIsListOpen((prev) => !prev), []);
  const closeList = useCallback(() => setIsListOpen(false), []);

  const handleSave = useCallback(async () => {
    if (!applicationId) {
      toast.error("Can't save — no application is bound.");
      return;
    }
    const store = useApplicationPendingChangesStore.getState();
    // Read entries directly from the store at click time to avoid re-rendering
    // on every keystroke as pending values change.
    const entryList = Object.values(store.changes);
    if (entryList.length === 0) return;
    // Guard against a double-press (or ⌘S while the first save is still in
    // flight) re-dispatching an already-cleared change set.
    if (store.saveState === "saving") return;
    store.setSaveState("saving", entryList.length);

    const app = queryClient.getQueryData<ChecklistApp>([...SINGLE_APPLICATION_KEY, applicationId]);

    // Group entries by saver name (default = applicationFields) and dispatch
    // each group through its registered handler. Entries that map to the same
    // saver get batched together; entries with custom savers (e.g. property
    // valuations) fan out through their own write paths in parallel.
    const groups = _.groupBy(entryList, (e) => (e.saver?.name ?? "applicationFields") as SaverName);

    const ctx = { applicationId, app, queryClient, saveFields };

    const saverPromises: Promise<unknown>[] = [];
    let failed = false;
    const failedEntries: PendingEntry[] = [];
    for (const [name, entries] of Object.entries(groups)) {
      try {
        const result = runSaver(name as SaverName, entries, ctx);
        if (result instanceof Promise) {
          saverPromises.push(
            result.catch((err) => {
              failed = true;
              failedEntries.push(...entries);
              console.error(`Saver ${name} failed`, err);
              toast.error("Failed to save some changes — please try again.");
            })
          );
        }
      } catch (err) {
        failed = true;
        failedEntries.push(...entries);
        console.error(`Saver ${name} threw`, err);
        toast.error("Failed to save changes — please try again.");
      }
    }

    useApplicationPendingChangesStore.getState().discardAll();
    setIsListOpen(false);

    // Wait for the PATCH mutations to settle before invalidating; otherwise the
    // GET refetch races the in-flight PATCH and can overwrite the optimistic
    // update with pre-mutation data, forcing the user to refresh again.
    await Promise.allSettled(saverPromises);

    // Now that the writes have landed, sync the rest of the app's cached state
    // with the server — saved fields can trigger backend change hooks
    // (status transitions, derived fields, checklist recomputation, etc.)
    // that aren't reflected in the optimistic cache update.
    //
    // Awaited, not fired and forgotten: refetching the full application graph is
    // most of the several seconds a save takes, and the screen keeps rendering
    // pre-hook values until it lands. Holding "Saving…" across it is the
    // difference between a slow save and one that looks like it didn't take.
    await Promise.allSettled([
      queryClient.invalidateQueries({ queryKey: [...SINGLE_APPLICATION_KEY, applicationId] }),
      queryClient.invalidateQueries({
        queryKey: ["checklist", ...APPLICATION_KEY, applicationId],
        exact: false,
      }),
    ]);
    // Product-rate matching runs a heavier server-side recompute. Fire it
    // fire-and-forget so the save itself isn't blocked; the matching
    // endpoint owns its own cache update when it completes.
    fraction.refreshProductMatching(applicationId).catch(() => {
      // Non-critical: product matching can lag behind field saves without
      // breaking the main save flow. Suppress errors here so failed
      // matching doesn't surface as a noisy toast on every save.
    });

    // Acknowledge, then get out of the way. Staged edits made while the save was
    // in flight take priority — the bar goes straight back to counting them
    // rather than flashing "Saved" over changes that aren't.
    const latest = useApplicationPendingChangesStore.getState();
    if (latest.saveState !== "saving") return;
    if (failed) {
      if (latest.applicationId === applicationId) {
        for (const entry of failedEntries) {
          if (!(entry.path in latest.changes)) latest.stage(entry);
        }
      }
      // The toast already said what happened; a "Saved" tick after it would be a lie.
      latest.setSaveState("idle");
      return;
    }
    latest.setSaveState("saved");
    savedTimer.current = window.setTimeout(() => {
      const current = useApplicationPendingChangesStore.getState();
      if (current.saveState === "saved") {
        current.setSaveState("idle");
      }
    }, SAVED_VISIBLE_MS);
  }, [applicationId, queryClient, saveFields]);

  const handleCancel = useCallback(() => {
    if (count === 0) return;
    // Mid-save the writes are already gone to the server; discarding here would
    // only clear edits staged since, which isn't what Cancel means.
    if (useApplicationPendingChangesStore.getState().saveState === "saving") return;
    if (count > 1) {
      if (!window.confirm(`Discard ${count} unsaved changes?`)) return;
    }
    useApplicationPendingChangesStore.getState().discardAll();
    setIsListOpen(false);
  }, [count]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "s") {
        e.preventDefault();
        handleSave();
      } else if (e.key === "Escape") {
        // If the list panel is open, Esc just closes it. Otherwise fall
        // through to the cancel-all flow.
        if (isListOpen) {
          setIsListOpen(false);
        } else {
          handleCancel();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleSave, handleCancel, isListOpen]);

  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // While a save runs, the bar becomes a progress indicator rather than a
  // control: there's nothing left to cancel and nothing left to save, and the
  // count it was showing has already been cleared out of the store.
  if (isSaving || saveState === "saved") {
    return (
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2"
        style={{ zIndex: pillZIndex }}
        onMouseDown={stopPropagation}
        onClick={stopPropagation}
      >
        <div
          role="status"
          aria-live="polite"
          className="flex flex-row items-center gap-2.5 bg-card dark:border dark:border-gray-700 rounded-full shadow-lg px-4 py-2"
        >
          {isSaving ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-foreground/70" />
          ) : (
            <CheckIcon className="h-3.5 w-3.5 text-green" />
          )}
          <span className="text-sm font-medium text-foreground">
            {isSaving
              ? `Saving ${savingCount} ${savingCount === 1 ? "change" : "changes"}…`
              : `Saved ${savingCount} ${savingCount === 1 ? "change" : "changes"}`}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2"
      style={{ zIndex: pillZIndex }}
      onMouseDown={stopPropagation}
      onClick={stopPropagation}
    >
      {isListOpen && <PendingChangesListPanel onClose={closeList} />}
      <div className="flex flex-row items-center gap-3 bg-card dark:border dark:border-gray-700 text-primary-foreground rounded-full shadow-lg px-4 py-2">
        <button
          type="button"
          onClick={toggleList}
          aria-label={isListOpen ? "Hide pending changes" : "Show pending changes"}
          aria-expanded={isListOpen}
          className={cn(
            "flex items-center justify-center h-6 w-6 rounded-full hover:bg-card/10 transition-colors",
            isListOpen && "bg-card/15"
          )}
        >
          <ListIcon className="h-3.5 w-3.5 text-foreground" />
        </button>
        <span className="text-sm font-medium text-foreground">
          {count} unsaved {count === 1 ? "change" : "changes"}
        </span>
        <Button variant="ghost" size="sm" onClick={handleCancel} className="text-foreground hover:bg-card/10">
          Cancel
        </Button>
        <Button variant="green" size="sm" onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
});

PendingChangesSaveBarInner.displayName = "PendingChangesSaveBarInner";

/**
 * Expanded list of currently-pending changes — each row shows the path and the
 * user's staged value, with an X to discard that single entry without
 * affecting the others. Mounted only while open so the full-changes
 * subscription (which re-renders on every keystroke) doesn't run otherwise.
 */
const PendingChangesListPanel = memo(({ onClose }: { onClose: () => void }) => {
  const changes = useApplicationPendingChangesStore((s) => s.changes);
  const entries = Object.values(changes);

  const handleDiscard = useCallback((path: string) => {
    useApplicationPendingChangesStore.getState().discard(path);
  }, []);

  return (
    <>
      {/* Click-outside catcher. Covers the viewport so any click outside the
          panel closes it; the panel itself stops propagation so internal clicks
          still work. */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      <div
        className="relative z-50 bg-card text-foreground rounded-lg shadow-xl border border-background/10 min-w-[320px] max-w-[420px] max-h-64 overflow-y-auto"
        onClick={stopPropagation}
      >
        <div className="sticky top-0 bg-card px-3 py-2 border-b border-background/10 text-[10px] uppercase tracking-wide text-foreground/60">
          Unsaved changes ({entries.length})
        </div>
        {entries.length === 0 ? (
          <div className="px-3 py-2 text-xs text-foreground/60">No pending changes.</div>
        ) : (
          <ul className="divide-y divide-background/5">
            {entries.map((entry) => (
              <PendingChangeRow key={entry.path} entry={entry} onDiscard={handleDiscard} />
            ))}
          </ul>
        )}
      </div>
    </>
  );
});

PendingChangesListPanel.displayName = "PendingChangesListPanel";

const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

/** Pretty-print a dot-path as a human-readable label (e.g. "solicitor.realEstateCertified" → "Real Estate Certified"). */
const labelForPath = (path: string) => {
  const last = path.split(".").pop() || path;
  return _.startCase(last);
};

const PendingChangeRow = memo(
  ({ entry, onDiscard }: { entry: PendingEntry; onDiscard: (path: string) => void }) => {
    const handleClick = useCallback(() => onDiscard(entry.path), [entry.path, onDiscard]);

    // Run the raw input through the serializer to get the storage shape
    // (e.g. "1234.56" → 123456 cents for currency fields), then hand it to
    // the shared field formatter so currency / percentage / date / enum /
    // boolean values render the same as elsewhere in the UI.
    let displayContent: React.ReactNode;
    const isEmpty = entry.rawValue === null || entry.rawValue === undefined || entry.rawValue === "";
    if (isEmpty) {
      displayContent = <em className="text-foreground not-italic">(empty)</em>;
    } else {
      try {
        const storageValue = SERIALIZERS[entry.serializer].toStorage(entry.rawValue);
        const formatted = formatters.field.formatFieldValue(entry.path, storageValue);
        displayContent = formatted || String(entry.rawValue);
      } catch {
        displayContent = String(entry.rawValue);
      }
    }

    return (
      <li className="flex flex-row items-center gap-2 px-3 py-1.5 text-xs">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] text-foreground/60 truncate">{labelForPath(entry.path)}</div>
          <div className="truncate">{displayContent}</div>
          {entry.proposal ? (
            <div className="mt-0.5 text-[10px] leading-tight text-purple-300">
              Agent proposal: {entry.proposal.reason}
              {entry.proposal.evidenceDocumentIds?.length
                ? ` · evidence ${entry.proposal.evidenceDocumentIds.join(", ")}`
                : ""}
              {entry.proposal.evidence?.length
                ? ` · ${entry.proposal.evidence
                    .map(
                      (evidence) =>
                        `${evidence.documentId}${evidence.pages?.length ? ` p.${evidence.pages.join(",")}` : ""}${
                          evidence.method ? ` (${evidence.method})` : ""
                        }`
                    )
                    .join("; ")}`
                : ""}
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={handleClick}
          aria-label={`Discard ${entry.path}`}
          className="flex items-center justify-center h-5 w-5 rounded hover:bg-card/10 transition-colors shrink-0"
        >
          <XIcon className="h-3 w-3" />
        </button>
      </li>
    );
  }
);

PendingChangeRow.displayName = "PendingChangeRow";
